<template>
  <div
    class="mcp-page"
    :class="{ 'mcp-page-upsell': !plan_allowed }"
  >
    <McpUpsell
      v-if="!plan_allowed"
      :can-upgrade="can_upgrade"
      :upgrade-url="upgrade_url"
    />

    <template v-else>
      <div
        v-if="showCallBanner"
        class="zone-alert zone-alert-cta-row"
        data-testid="mcp-early-users-banner"
      >
        <div class="zone-alert-content">
          <div class="zone-alert-head">
            <i class="material-symbols-rounded">lightbulb</i>
            <b>Talk to us — we’re building this with early users</b>
          </div>
          <p>Tell us what you want your AI assistant to do with your Hubstaff data.</p>
        </div>
        <div class="zone-alert-cta">
          <a
            :href="book_call_url"
            class="zbtn zbtn-primary"
            target="_blank"
            rel="noopener"
          >
            <i class="material-symbols-rounded">calendar_today</i>
            Book a 30-minute call
          </a>
          <button
            type="button"
            class="zone-alert-close"
            aria-label="Dismiss"
            @click="callBannerDismissed = true"
          >
            <i class="material-symbols-rounded">close</i>
          </button>
        </div>
      </div>

      <div class="zone-section">
        <div class="zone-section-head">
          <h2 data-testid="mcp-page-heading">Connect MCP to your AI tool</h2>
          <p>The same Hubstaff address works in every AI tool. Pick yours below to see the exact steps.</p>
          <p>
            An owner or a manager requests this once for the organization. After that everyone on the team can
            add the server in their own AI client, sign in with their Hubstaff account, and start asking
            questions — there is nothing else for them to set up.
          </p>
        </div>

        <div
          v-if="!access_enabled"
          class="zone-alert"
          data-testid="mcp-access-pending"
        >
          <div class="zone-alert-content">
            <div class="zone-alert-head">
              <i class="material-symbols-rounded">info</i>
              <b>Hubstaff MCP isn’t enabled for this organization yet</b>
            </div>
            <p>
              You can go through the setup now, but signing in will fail until it is switched on — your AI app
              will say “MCP is not enabled for organization …”.
              <a
                :href="support_guide_url"
                target="_blank"
                rel="noopener"
              >Contact Hubstaff support</a> to have it enabled for this organization.
            </p>
          </div>
        </div>

        <McpSetupSteps
          :mcp-url="mcp_url"
          :starter-prompt="starter_prompt"
        />
      </div>

      <div class="zone-section">
        <div class="zone-section-head">
          <h2>Connected AI apps</h2>
        </div>

        <McpConnectedApps :apps="connected_apps" />
      </div>

      <div class="zone-section">
        <div class="zone-section-head">
          <h2>How MCP works</h2>
          <p>MCP lets an AI app answer questions using the Hubstaff data you can already see.</p>
        </div>

        <McpHowItWorks :developer-portal-url="developer_portal_url" />
      </div>
    </template>
  </div>
</template>

<script>
import McpSetupSteps from '~/components/hubstaff_mcp/McpSetupSteps.vue';
import McpConnectedApps from '~/components/hubstaff_mcp/McpConnectedApps.vue';
import McpHowItWorks from '~/components/hubstaff_mcp/McpHowItWorks.vue';
import McpUpsell from '~/components/hubstaff_mcp/McpUpsell.vue';

export default {
  name: 'HubstaffMcp',
  components: { McpSetupSteps, McpConnectedApps, McpHowItWorks, McpUpsell },
  props: {
    organization: { type: Object, required: true },
    plan_allowed: { type: Boolean, default: false },
    access_enabled: { type: Boolean, default: false },
    can_upgrade: { type: Boolean, default: false },
    upgrade_url: { type: String, required: true },
    mcp_url: { type: String, required: true },
    starter_prompt: { type: String, required: true },
    support_guide_url: { type: String, required: true },
    developer_portal_url: { type: String, required: true },
    connected_apps: { type: Array, default: () => [] },
    book_call_url: { type: String, default: '' },
  },
  data() {
    return { callBannerDismissed: false };
  },
  computed: {
    showCallBanner() {
      return Boolean(this.book_call_url)
        && !this.callBannerDismissed
        && (!this.access_enabled || !this.connected_apps.length);
    },
  },
};
</script>

<style>
/* Tailwind utilities are not generated for .vue files, so the Zone tokens are carried here. */
.mcp-page {
  --gray-50: var(--z-gray-50, #f9fafb);
  --gray-100: var(--z-gray-100, #f3f4f6);
  --gray-200: var(--z-gray-200, #e5e7eb);
  --gray-300: var(--z-gray-300, #d1d5db);
  --gray-400: var(--z-gray-400, #9ca3af);
  --gray-500: var(--z-gray-500, #6b7280);
  --gray-600: var(--z-gray-600, #4b5563);
  --gray-700: var(--z-gray-700, #374151);
  --gray-800: var(--z-gray-800, #1f2937);
  --gray-900: var(--z-gray-900, #111827);
  --primary-50: var(--z-primary-50, #eaf6ff);
  --primary-100: var(--z-primary-100, #d4edff);
  --primary-500: var(--z-primary-500, #2aa7ff);
  --primary-600: var(--z-primary-600, #2f8af4);
  --primary-700: var(--z-primary-700, #0168dd);
  --primary-800: var(--z-primary-800, #0a4b96);
  --green-100: var(--z-green-100, #def7ec);
  --green-600: var(--z-green-600, #057a55);
  --green-800: var(--z-green-800, #03543f);
  --purple-100: var(--z-purple-100, #e8d2ff);
  --purple-800: var(--z-purple-800, #5427a7);
  --indigo-600: var(--z-indigo-600, #5850ec);
  --rounded-sm: var(--z-rounded-sm, 2px);
  --rounded-md: var(--z-rounded-md, 6px);
  --rounded-lg: var(--z-rounded-lg, 8px);
}
</style>

<style scoped>
.mcp-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
  padding: 0 32px 80px;
}

/* The upsell is a centred mat, so it gets the whole width rather than the reading column. */
.mcp-page-upsell {
  max-width: none;
}

.zone-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.zone-section-head h2 {
  margin: 0 0 4px;
  color: var(--gray-900);
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}

.zone-section-head p {
  margin: 0;
  color: var(--gray-500);
  font-size: 14px;
  line-height: 20px;
}

.zone-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--primary-700);
  border-radius: var(--rounded-lg);
  background: var(--primary-50);
  overflow: clip;
}

.zone-alert-content {
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.zone-alert-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zone-alert-head .material-symbols-rounded {
  font-size: 18px;
  line-height: 16px;
  color: var(--primary-800);
}

.zone-alert-head b {
  color: var(--primary-800);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.zone-alert-content p {
  margin: 0;
  color: var(--primary-800);
  font-size: 14px;
  line-height: 20px;
}

.zone-alert-cta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: none;
}

.zone-alert-close {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--primary-700);
  cursor: pointer;
}

.zone-alert-close .material-symbols-rounded {
  font-size: 18px;
  line-height: 16px;
}

.zbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-height: 40px;
  padding: 10px 20px;
  border: 1px solid transparent;
  border-radius: var(--rounded-md);
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
}

.zbtn .material-symbols-rounded {
  font-size: 18px;
  line-height: 16px;
}

.zbtn-primary {
  background: var(--primary-500);
  color: #fff;
}

.zbtn-primary:hover {
  background: var(--primary-600);
  color: #fff;
}
</style>
